import { Component, signal, inject, OnInit, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';

import { Auth } from '@core/services';
import { UserRole } from '@core/interfaces/role.interface';
import { Dashboard as DashboardService } from '@modules/dashboard/services/dashboard';
import { Reseller as ResellerService } from '@modules/reseller/services/reseller';
import { SubReseller as SubResellerService } from '@modules/sub-reseller/services/sub-reseller';
import { DateRangePicker } from '@shared/date-range-picker/date-range-picker';
import { Loader } from '@shared/loader/loader';
import { Title, Toaster } from '@shared/services';

interface DashboardUserOption {
  _id: string;
  label: string;
}

export interface MessageStatusStats {
  submit: number;
  sent: number;
  delivered: number;
  undelivered: number;
  cancelled: number;
  pending: number;
  failed: number;
}

export interface DashboardStats {
  single: MessageStatusStats;
  campaign: MessageStatusStats;
  totalCampaign: number;
}

interface StatusFieldConfig {
  key: keyof MessageStatusStats;
  label: string;
  cssClass: string;
}

interface StatusChartRow {
  label: string;
  stats: MessageStatusStats;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DateRangePicker, Loader],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss'
})
export class Dashboard implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(Auth);
  private dashboardService = inject(DashboardService);
  private resellerService = inject(ResellerService);
  private subResellerService = inject(SubResellerService);
  private titleService = inject(Title);
  private toasterService = inject(Toaster);

  isLoading = signal(false);
  selectedResellerId = signal<string | null>(null);
  selectedSubResellerId = signal<string | null>(null);
  userOptions = signal<DashboardUserOption[]>([]);
  subResellerOptions = signal<DashboardUserOption[]>([]);
  stats = signal<DashboardStats>(this.createEmptyStats());

  readonly statusFields: StatusFieldConfig[] = [
    { key: 'sent', label: 'Sent', cssClass: 'sent' },
    { key: 'delivered', label: 'Delivered', cssClass: 'delivered' },
    { key: 'undelivered', label: 'Undelivered', cssClass: 'undelivered' },
    { key: 'cancelled', label: 'Cancelled', cssClass: 'cancelled' },
    { key: 'pending', label: 'Pending', cssClass: 'pending' },
    { key: 'failed', label: 'Failed', cssClass: 'failed' }
  ];

  readonly chartLegendFields: StatusFieldConfig[] = [...this.statusFields];

  statusChartRows = computed((): StatusChartRow[] => {
    const s = this.stats();
    return [
      { label: 'Single', stats: s.single },
      { label: 'Campaign', stats: s.campaign }
    ];
  });

  dateRange: FormGroup = this.fb.group({
    range: [null]
  });

  isAdmin = computed(() => (this.authService.user?.role?.name ?? '') === UserRole.ADMIN);
  isReseller = computed(() => (this.authService.user?.role?.name ?? '') === UserRole.RESELLER);

  private rangeValue = toSignal(this.dateRange.get('range')!.valueChanges, { initialValue: null });

  constructor() {
    effect(() => {
      this.selectedResellerId();
      this.selectedSubResellerId();
      this.rangeValue();
      this.getMessageStats();
    });
  }

  ngOnInit() {
    this.titleService.setTitle('Dashboard');
    this.loadUserOptions();
    this.loadSubResellerOptions();
  }

  loadUserOptions(): void {
    if (!this.isAdmin()) {
      return;
    }

    this.resellerService
      .getResellers({ type: UserRole.RESELLER, page: 1, pageSize: 500 })
      .subscribe({
        next: (response) => {
          const list = response?.data ?? [];
          this.userOptions.set(this.mapUserOptions(list));
        },
        error: (error) => {
          this.toasterService.errorAlert(error.error?.message || 'Failed to load users for filter');
        }
      });
  }

  loadSubResellerOptions(): void {
    if (!this.isReseller() && !this.isAdmin()) {
      return;
    }

    const queryParams: Record<string, string | number> = {
      type: UserRole.SUB_RESELLER
    };

    this.subResellerService.getSubResellers(queryParams).subscribe({
      next: (response) => {
        const list = response?.data ?? [];
        this.subResellerOptions.set(this.mapUserOptions(list));
      },
      error: (error) => {
        this.toasterService.errorAlert(
          error.error?.message || 'Failed to load sub resellers for filter'
        );
      }
    });
  }

  getMessageStats() {
    this.isLoading.set(true);
    const queryParams: Record<string, string> = {};

    if (this.selectedSubResellerId()) {
      queryParams['user'] = this.selectedSubResellerId()!;
    } else if (this.selectedResellerId()) {
      queryParams['user'] = this.selectedResellerId()!;
    }

    const range = this.dateRange.get('range')?.value;
    if (range?.start) {
      queryParams['startDate'] = range.start;
    }
    if (range?.end) {
      queryParams['endDate'] = range.end;
    }

    this.dashboardService.messageStats(queryParams).subscribe({
      next: (response: { data: any }) => {
        this.stats.set(this.normalizeStats(response.data));
        this.isLoading.set(false);
      },
      error: (error) => {
        this.toasterService.errorAlert(error.error.message);
        this.isLoading.set(false);
      }
    });
  }

  isFilterApplied() {
    return (
      this.dateRange.get('range')?.value !== null ||
      this.selectedResellerId() !== null ||
      this.selectedSubResellerId() !== null
    );
  }

  clearFilter() {
    this.selectedResellerId.set(null);
    this.selectedSubResellerId.set(null);
    this.dateRange.reset({ range: null });
  }

  onUserSelect(userId: string): void {
    const id = userId || null;
    this.selectedResellerId.set(id);
    if (id) {
      this.selectedSubResellerId.set(null);
    }
  }

  onSubResellerSelect(userId: string): void {
    const id = userId || null;
    this.selectedSubResellerId.set(id);
    if (id) {
      this.selectedResellerId.set(null);
    }
  }

  private mapUserOptions(list: any[]): DashboardUserOption[] {
    return list
      .map((item) => {
        const id = item._id ?? item.id ?? item.userId;
        if (!id) return null;
        const label =
          `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim() || item.email || 'Unknown user';
        return { _id: String(id), label };
      })
      .filter((item): item is DashboardUserOption => item !== null);
  }

  typeTotal(bucket: MessageStatusStats): number {
    if (bucket.submit > 0) {
      return bucket.submit;
    }

    return this.statusBreakdownSum(bucket);
  }

  statusChartTotal(bucket: MessageStatusStats): number {
    return this.typeTotal(bucket);
  }

  chartStackFields(bucket: MessageStatusStats): StatusFieldConfig[] {
    const breakdown = this.statusBreakdownSum(bucket);
    if (bucket.submit > 0 && bucket.submit === breakdown) {
      return this.statusFields;
    }

    return this.chartLegendFields;
  }

  statusSegmentWidth(value: number, bucket: MessageStatusStats): number {
    const total = this.statusChartTotal(bucket);
    if (!total || !value) {
      return 0;
    }

    return (value / total) * 100;
  }

  private statusBreakdownSum(bucket: MessageStatusStats): number {
    return (
      bucket.sent +
      bucket.delivered +
      bucket.undelivered +
      bucket.cancelled +
      bucket.pending +
      bucket.failed
    );
  }

  private createEmptyStats(): DashboardStats {
    return {
      single: this.createEmptyStatusStats(),
      campaign: this.createEmptyStatusStats(),
      totalCampaign: 0
    };
  }

  private createEmptyStatusStats(): MessageStatusStats {
    return {
      submit: 0,
      sent: 0,
      delivered: 0,
      undelivered: 0,
      cancelled: 0,
      pending: 0,
      failed: 0
    };
  }

  private normalizeStats(data: any): DashboardStats {
    if (!data) {
      return this.createEmptyStats();
    }

    if (data.single || data.campaign) {
      return {
        single: this.parseStatusBucket(data.single),
        campaign: this.parseStatusBucket(data.campaign),
        totalCampaign: data.totalCampaign ?? 0
      };
    }

    return {
      single: this.parseStatusBucket(data),
      campaign: this.createEmptyStatusStats(),
      totalCampaign: 0
    };
  }

  private parseStatusBucket(bucket: any): MessageStatusStats {
    if (!bucket) {
      return this.createEmptyStatusStats();
    }

    return {
      submit: bucket.Submit ?? bucket.submit ?? 0,
      sent: bucket.Sent ?? bucket.sent ?? 0,
      delivered: bucket.Delivered ?? bucket.delivered ?? 0,
      undelivered: bucket.Undelivered ?? bucket.undelivered ?? 0,
      cancelled: bucket.Cancelled ?? bucket.Cancel ?? bucket.cancelled ?? 0,
      pending: bucket.Pending ?? bucket.pending ?? 0,
      failed: bucket.Failed ?? bucket.failed ?? 0
    };
  }
}
